import { PRIORITIES, PRIORITY_META, SNOOZE_PRESETS, type BulkActionInput } from '@taskos/shared';
import { useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { useState } from 'react';
import { errorMessage, post } from '../../api/client';
import { invalidateTasks, useCategories, useDirectory, useProjects } from '../../api/queries';
import { useSession } from '../../api/session';
import { useToast } from '../../components/toast';
import { Button, ConfirmDialog, Field, Menu, Modal } from '../../components/ui';

type Pending = { action: BulkActionInput; label: string } | null;

/**
 * Bulk actions over selected tasks. The server applies them in ONE transaction and reports which
 * items were skipped (not permitted / already in that state). Destructive ones need confirmation.
 */
export function BulkBar({ ids, onClear }: { ids: string[]; onClear: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { can } = useSession();
  const dir = useDirectory();
  const projects = useProjects();
  const cats = useCategories();
  const [confirm, setConfirm] = useState<Pending>(null);
  const [moveOpen, setMoveOpen] = useState(false);
  const [move, setMove] = useState({ projectId: '', categoryId: '' });
  const [tagOpen, setTagOpen] = useState(false);
  const [tag, setTag] = useState('');

  const run = async (action: BulkActionInput, label: string) => {
    try {
      const r = await post<{ updated: number; skipped: Array<{ id: string; reason: string }> }>('/api/tasks/bulk', action);
      const reasons = [...new Set(r.skipped.map((s) => s.reason))].join(', ');
      toast.push({
        kind: r.skipped.length && !r.updated ? 'warning' : 'success',
        title: `${label}: ${r.updated} task${r.updated === 1 ? '' : 's'} updated`,
        message: r.skipped.length ? `${r.skipped.length} skipped (${reasons})` : undefined,
      });
      onClear();
    } catch (e) {
      toast.push({ kind: 'error', title: `${label} failed — nothing was changed`, message: errorMessage(e), action: { label: 'Retry', onClick: () => run(action, label) } });
    } finally {
      invalidateTasks(qc);
    }
  };
  if (!ids.length) return null;
  const taskIds = ids;
  return (
    <div className="bulk-bar" role="toolbar" aria-label="Bulk actions">
      <strong>{ids.length} selected</strong>
      <Button size="sm" onClick={() => run({ action: 'complete', taskIds }, 'Complete')}>
        ✓ Complete
      </Button>
      <Menu
        label="Snooze selected"
        items={SNOOZE_PRESETS.map((p) => ({ label: p.label, onSelect: () => run({ action: 'snooze', taskIds, snooze: { preset: p.id } }, 'Snooze') }))}
        trigger={(p) => (
          <Button size="sm" {...p}>
            ⏰ Snooze
          </Button>
        )}
      />
      <Menu
        label="Change priority"
        items={PRIORITIES.map((p) => ({ label: `${PRIORITY_META[p].emoji} ${PRIORITY_META[p].label}`, onSelect: () => run({ action: 'priority', taskIds, priority: p }, 'Priority') }))}
        trigger={(p) => (
          <Button size="sm" {...p}>
            🔥 Priority
          </Button>
        )}
      />
      {(can('task.assign') || can('task.reassign')) && (
        <Menu
          label="Assign selected"
          items={[
            { label: 'Unassign', onSelect: () => run({ action: 'assign', taskIds, assigneeId: null }, 'Assign') },
            ...(dir.data ?? []).filter((u) => u.isActive).map((u) => ({ label: u.name, onSelect: () => run({ action: 'assign', taskIds, assigneeId: u.id }, 'Assign') })),
          ]}
          trigger={(p) => (
            <Button size="sm" {...p}>
              👤 Assign
            </Button>
          )}
        />
      )}
      <Button size="sm" onClick={() => setMoveOpen(true)}>
        📁 Move
      </Button>
      <Button size="sm" onClick={() => setTagOpen(true)}>
        # Tag
      </Button>
      <Button size="sm" onClick={() => setConfirm({ action: { action: 'cancel', taskIds }, label: 'Cancel' })}>
        ✖ Cancel
      </Button>
      <Button size="sm" onClick={() => setConfirm({ action: { action: 'archive', taskIds }, label: 'Archive' })}>
        🗄 Archive
      </Button>
      <span className="spacer" />
      <Button size="sm" variant="ghost" icon={<X size={15} />} onClick={onClear}>
        Clear
      </Button>

      <ConfirmDialog
        open={!!confirm}
        title={`${confirm?.label} ${ids.length} task${ids.length === 1 ? '' : 's'}?`}
        message={confirm?.label === 'Archive' ? 'Archived tasks leave active views but keep all history.' : 'Cancelled tasks stay in history and can be reopened.'}
        confirmLabel={confirm?.label ?? 'Confirm'}
        danger
        onCancel={() => setConfirm(null)}
        onConfirm={async () => {
          if (!confirm) return;
          await run(confirm.action, confirm.label);
          setConfirm(null);
        }}
      />
      <Modal
        open={moveOpen}
        onClose={() => setMoveOpen(false)}
        title="Move to project / category"
        size="narrow"
        footer={
          <>
            <Button onClick={() => setMoveOpen(false)}>Cancel</Button>
            <Button
              variant="primary"
              onClick={async () => {
                await run(
                  {
                    action: 'move',
                    taskIds,
                    ...(move.projectId ? { projectId: move.projectId === 'none' ? null : move.projectId } : {}),
                    ...(move.categoryId ? { categoryId: move.categoryId === 'none' ? null : move.categoryId } : {}),
                  },
                  'Move',
                );
                setMoveOpen(false);
              }}
              disabled={!move.projectId && !move.categoryId}
            >
              Move
            </Button>
          </>
        }
      >
        <div className="stack">
          <Field label="Project" htmlFor="bm-project">
            <select id="bm-project" className="select" value={move.projectId} onChange={(e) => setMove({ ...move, projectId: e.target.value })}>
              <option value="">(unchanged)</option>
              <option value="none">No project</option>
              {(projects.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Category" htmlFor="bm-category">
            <select id="bm-category" className="select" value={move.categoryId} onChange={(e) => setMove({ ...move, categoryId: e.target.value })}>
              <option value="">(unchanged)</option>
              <option value="none">No category</option>
              {(cats.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
      </Modal>
      <Modal
        open={tagOpen}
        onClose={() => setTagOpen(false)}
        title="Add tag"
        size="narrow"
        footer={
          <>
            <Button onClick={() => setTagOpen(false)}>Cancel</Button>
            <Button
              variant="primary"
              disabled={!tag.trim()}
              onClick={async () => {
                await run({ action: 'tag', taskIds, tag: tag.trim().replace(/^#/, '') }, 'Tag');
                setTagOpen(false);
                setTag('');
              }}
            >
              Add tag
            </Button>
          </>
        }
      >
        <Field label="Tag" htmlFor="bm-tag">
          <input id="bm-tag" className="input" value={tag} maxLength={40} onChange={(e) => setTag(e.target.value)} />
        </Field>
      </Modal>
    </div>
  );
}
